/**
 * Options Trading Page
 * Professional options analysis with IV, Greeks, and strategy selection
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { optionsAPI, alpacaAPI, botAPI } from '../services/api';
import toast from 'react-hot-toast';
import Navigation from '../components/Navigation';
import {
  RefreshCw, TrendingUp, TrendingDown, Activity,
  Target, AlertTriangle, CheckCircle, XCircle, Search,
  BarChart3, Zap, Shield, Clock, DollarSign, Percent,
  ChevronDown, ChevronUp, Info, Calendar, Filter, Bot, Settings, Save
} from 'lucide-react';

// IV Gauge component
const IVGauge = ({ ivRank, size = 'normal' }) => {
  const getColor = () => {
    if (ivRank >= 70) return 'text-accent-red';
    if (ivRank <= 30) return 'text-accent-green';
    return 'text-accent-yellow';
  };

  const getLabel = () => {
    if (ivRank >= 70) return 'HIGH';
    if (ivRank <= 30) return 'LOW';
    return 'NORMAL';
  };

  const circumference = 2 * Math.PI * 45;
  const strokeDashoffset = circumference - (ivRank / 100) * circumference;

  return (
    <div className={`relative ${size === 'small' ? 'w-20 h-20' : 'w-32 h-32'}`}>
      <svg className="transform -rotate-90 w-full h-full">
        <circle
          cx="50%"
          cy="50%"
          r="45%"
          stroke="currentColor"
          strokeWidth="8"
          fill="none"
          className="text-bg-tertiary"
        />
        <circle
          cx="50%"
          cy="50%"
          r="45%"
          stroke="currentColor"
          strokeWidth="8"
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          className={getColor()}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className={`font-mono font-bold ${size === 'small' ? 'text-lg' : 'text-2xl'} ${getColor()}`}>
          {ivRank}
        </span>
        <span className={`text-text-muted ${size === 'small' ? 'text-xs' : 'text-sm'}`}>{getLabel()}</span>
      </div>
    </div>
  );
};

// Strategy Card component
const StrategyCard = ({ strategy, isSelected, onClick }) => {
  const directionColors = {
    bullish: 'border-accent-green text-accent-green',
    bearish: 'border-accent-red text-accent-red',
    neutral: 'border-accent-yellow text-accent-yellow',
  };

  return (
    <div
      onClick={onClick}
      className={`card p-4 cursor-pointer transition-all ${
        isSelected ? 'ring-2 ring-accent-blue border-accent-blue' : ''
      }`}
    >
      <div className="flex items-start justify-between mb-2">
        <h4 className="font-semibold">{strategy.name}</h4>
        <span className={`badge ${directionColors[strategy.direction]} border`}>
          {strategy.direction}
        </span>
      </div>
      <p className="text-text-muted text-sm mb-3">{strategy.description}</p>
      <div className="flex items-center gap-2 text-xs">
        <span className={`px-2 py-0.5 rounded ${
          strategy.ivPreference === 'high' ? 'bg-accent-red/15 text-accent-red' : 'bg-accent-green/15 text-accent-green'
        }`}>
          {strategy.ivPreference === 'high' ? 'Sell Premium' : 'Buy Premium'}
        </span>
        <span className={`px-2 py-0.5 rounded ${
          strategy.riskType === 'defined' ? 'bg-accent-blue/15 text-accent-blue' : 'bg-accent-yellow/15 text-accent-yellow'
        }`}>
          {strategy.riskType === 'defined' ? 'Defined Risk' : 'Undefined Risk'}
        </span>
      </div>
      {strategy.suitability && (
        <div className={`mt-2 text-xs ${
          strategy.suitability === 'optimal' ? 'text-accent-green' : 'text-accent-yellow'
        }`}>
          {strategy.suitability === 'optimal' ? 'Optimal for current conditions' : 'Acceptable'}
        </div>
      )}
    </div>
  );
};

// Scanner Result Row
const ScannerRow = ({ result, onAnalyze }) => {
  return (
    <tr className="border-b border-border-color hover:bg-bg-tertiary transition-all">
      <td className="py-3 px-4">
        <span className="font-mono font-bold">{result.symbol}</span>
      </td>
      <td className="py-3 px-4">
        <span className="font-mono">${result.currentPrice?.toFixed(2)}</span>
      </td>
      <td className="py-3 px-4">
        <div className="flex items-center gap-2">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
            result.ivRank >= 70 ? 'bg-accent-red/15 text-accent-red' :
            result.ivRank <= 30 ? 'bg-accent-green/15 text-accent-green' :
            'bg-accent-yellow/15 text-accent-yellow'
          }`}>
            {result.ivRank}
          </div>
        </div>
      </td>
      <td className="py-3 px-4">
        <span className={`badge ${
          result.trend === 'bullish' ? 'badge-green' :
          result.trend === 'bearish' ? 'badge-red' : 'badge-yellow'
        }`}>
          {result.trend}
        </span>
      </td>
      <td className="py-3 px-4">
        <span className="text-text-secondary text-sm">{result.regime}</span>
      </td>
      <td className="py-3 px-4">
        {result.hasOpportunity ? (
          <span className="text-accent-green text-sm">{result.topStrategy}</span>
        ) : (
          <span className="text-text-muted text-sm">No trade</span>
        )}
      </td>
      <td className="py-3 px-4">
        <button
          onClick={() => onAnalyze(result.symbol)}
          className="btn-secondary text-sm py-1 px-3"
        >
          Analyze
        </button>
      </td>
    </tr>
  );
};

// Main Options Page
const Options = () => {
  const { hasAlpacaKeys } = useAuth();
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [scanning, setScanning] = useState(false);
  
  // Analysis state
  const [symbol, setSymbol] = useState('SPY');
  const [analysis, setAnalysis] = useState(null);
  const [strategies, setStrategies] = useState([]);
  const [selectedStrategy, setSelectedStrategy] = useState(null);
  const [marketBias, setMarketBias] = useState(null);
  
  // Scanner state
  const [scanResults, setScanResults] = useState([]);
  const [showScanner, setShowScanner] = useState(false);
  
  // Calendar state
  const [calendar, setCalendar] = useState(null);
  
  // Constants state
  const [constants, setConstants] = useState(null);
  
  // Bot configuration state
  const [botConfig, setBotConfig] = useState({
    optionsEnabled: false,
    optionsConfig: {
      preferDefinedRisk: true,
      maxIVRankForBuying: 70,
      minIVRankForSelling: 20,
      minDTE: 5,
      maxDTE: 45,
      maxRiskPerTrade: 0.02,
      maxOptionsAllocation: 0.20,
      maxContractsPerTrade: 10,
      longProfitTarget: 0.50,
      longStopLoss: 0.50,
      creditProfitTarget: 0.50,
      creditStopMultiplier: 2.0,
      minOpenInterest: 500,
      maxSpreadPercent: 0.10,
      minVolume: 100,
      avoidFirst15Minutes: true,
      closeBeforeExpiry: true,
    }
  });
  const [savingBotConfig, setSavingBotConfig] = useState(false);
  const [showBotConfig, setShowBotConfig] = useState(false);

  // Fetch bot config
  const fetchBotConfig = useCallback(async () => {
    try {
      const res = await botAPI.getConfig();
      if (res.data?.config) {
        setBotConfig({
          optionsEnabled: res.data.config.optionsEnabled || false,
          optionsConfig: {
            preferDefinedRisk: res.data.config.optionsConfig?.preferDefinedRisk !== false,
            maxIVRankForBuying: res.data.config.optionsConfig?.maxIVRankForBuying || 70,
            minIVRankForSelling: res.data.config.optionsConfig?.minIVRankForSelling || 20,
            minDTE: res.data.config.optionsConfig?.minDTE || 5,
            maxDTE: res.data.config.optionsConfig?.maxDTE || 45,
            maxRiskPerTrade: res.data.config.optionsConfig?.maxRiskPerTrade || 0.02,
            maxOptionsAllocation: res.data.config.optionsConfig?.maxOptionsAllocation || 0.20,
            maxContractsPerTrade: res.data.config.optionsConfig?.maxContractsPerTrade || 10,
            longProfitTarget: res.data.config.optionsConfig?.longProfitTarget || 0.50,
            longStopLoss: res.data.config.optionsConfig?.longStopLoss || 0.50,
            creditProfitTarget: res.data.config.optionsConfig?.creditProfitTarget || 0.50,
            creditStopMultiplier: res.data.config.optionsConfig?.creditStopMultiplier || 2.0,
            minOpenInterest: res.data.config.optionsConfig?.minOpenInterest || 500,
            maxSpreadPercent: res.data.config.optionsConfig?.maxSpreadPercent || 0.10,
            minVolume: res.data.config.optionsConfig?.minVolume || 100,
            avoidFirst15Minutes: res.data.config.optionsConfig?.avoidFirst15Minutes !== false,
            closeBeforeExpiry: res.data.config.optionsConfig?.closeBeforeExpiry !== false,
          }
        });
      }
    } catch (err) {
      console.error('Failed to fetch bot config:', err);
    }
  }, []);

  // Save bot config
  const saveBotConfig = async () => {
    setSavingBotConfig(true);
    try {
      await botAPI.updateConfig(botConfig);
      toast.success('Options bot settings saved');
    } catch (err) {
      toast.error('Failed to save settings');
      console.error(err);
    } finally {
      setSavingBotConfig(false);
    }
  };

  // Update a single config value
  const updateOptionsConfig = (key, value) => {
    setBotConfig(prev => ({
      ...prev,
      optionsConfig: {
        ...prev.optionsConfig,
        [key]: value
      }
    }));
  };

  // Fetch strategies and constants on mount
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [strategiesRes, calendarRes] = await Promise.all([
          optionsAPI.getStrategies().catch(() => null),
          optionsAPI.getCalendar().catch(() => null),
        ]);
        
        if (strategiesRes?.data) {
          setStrategies(strategiesRes.data.strategies);
          setConstants({
            ivThresholds: strategiesRes.data.ivThresholds,
            riskLimits: strategiesRes.data.riskLimits,
            dteCategories: strategiesRes.data.dteCategories,
          });
        }
        if (calendarRes?.data) {
          setCalendar(calendarRes.data);
        }
        
        // Also fetch bot config
        await fetchBotConfig();
      } catch (err) {
        console.error('Failed to fetch initial data:', err);
      } finally {
        setLoading(false);
      }
    };

    if (hasAlpacaKeys) {
      fetchInitialData();
    } else {
      setLoading(false);
    }
  }, [hasAlpacaKeys, fetchBotConfig]);

  // Analyze a symbol
  const analyzeSymbol = useCallback(async (sym = symbol, bias = marketBias) => {
    if (!sym) return;
    
    setAnalyzing(true);
    try {
      const res = await optionsAPI.analyze(sym.toUpperCase(), bias);
      setAnalysis(res.data);
      setSymbol(sym.toUpperCase());
      
      if (res.data.strategy) {
        setSelectedStrategy(res.data.strategy);
      }
      
      toast.success(`Analysis complete for ${sym.toUpperCase()}`);
    } catch (err) {
      toast.error('Analysis failed');
      console.error('Analysis error:', err);
    } finally {
      setAnalyzing(false);
    }
  }, [symbol, marketBias]);

  // Run scanner
  const runScanner = async () => {
    setScanning(true);
    try {
      const res = await optionsAPI.scan();
      setScanResults(res.data.results || []);
      setShowScanner(true);
      toast.success(`Scanned ${res.data.results?.length || 0} symbols`);
    } catch (err) {
      toast.error('Scanner failed');
      console.error('Scanner error:', err);
    } finally {
      setScanning(false);
    }
  };

  if (!hasAlpacaKeys) {
    return (
      <div className="min-h-screen p-6 flex items-center justify-center">
        <div className="card p-8 text-center max-w-md">
          <AlertTriangle className="w-12 h-12 text-accent-yellow mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2">Alpaca Required</h2>
          <p className="text-text-muted mb-4">Connect your Alpaca account to use options analysis.</p>
          <Link to="/settings" className="btn-primary">Go to Settings</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Navigation 
        title="Options Trading" 
        subtitle="Professional options analysis and strategy selection"
      />
      
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-6">
        {/* Action Bar */}
        <div className="flex items-center justify-end mb-6">
          <button
            onClick={runScanner}
            disabled={scanning}
            className="btn-secondary flex items-center gap-2"
          >
            {scanning ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            {scanning ? 'Scanning...' : 'Scan Market'}
          </button>
        </div>

      {/* Symbol Input & Bias Selection */}
      <div className="card p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-text-secondary text-sm mb-2">Symbol</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={symbol}
                onChange={(e) => setSymbol(e.target.value.toUpperCase())}
                placeholder="SPY"
                className="input flex-1 font-mono"
                onKeyPress={(e) => e.key === 'Enter' && analyzeSymbol()}
              />
              <button
                onClick={() => analyzeSymbol()}
                disabled={analyzing || !symbol}
                className="btn-primary px-6"
              >
                {analyzing ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Analyze'}
              </button>
            </div>
          </div>
          <div>
            <label className="block text-text-secondary text-sm mb-2">Market Bias (Optional)</label>
            <div className="flex gap-2">
              {['bullish', 'neutral', 'bearish'].map(bias => (
                <button
                  key={bias}
                  onClick={() => setMarketBias(marketBias === bias ? null : bias)}
                  className={`flex-1 py-2 px-3 rounded-lg border transition-all capitalize ${
                    marketBias === bias
                      ? bias === 'bullish' ? 'bg-accent-green/15 border-accent-green text-accent-green' :
                        bias === 'bearish' ? 'bg-accent-red/15 border-accent-red text-accent-red' :
                        'bg-accent-yellow/15 border-accent-yellow text-accent-yellow'
                      : 'border-border-color text-text-secondary hover:border-text-muted'
                  }`}
                >
                  {bias}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-text-secondary text-sm mb-2">Quick Select</label>
            <div className="flex gap-2 flex-wrap">
              {['SPY', 'QQQ', 'AAPL', 'NVDA', 'TSLA'].map(s => (
                <button
                  key={s}
                  onClick={() => { setSymbol(s); analyzeSymbol(s); }}
                  className="px-3 py-2 rounded-lg bg-bg-tertiary text-text-secondary hover:bg-accent-blue/15 hover:text-accent-blue transition-all text-sm font-mono"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Main Analysis Grid */}
      {analysis && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {/* IV Analysis */}
          <div className="card p-6">
            <h3 className="font-display text-lg font-semibold mb-4 flex items-center gap-2">
              <Activity className="w-5 h-5 text-accent-purple" />
              Implied Volatility
            </h3>
            <div className="flex items-center justify-center mb-4">
              <IVGauge ivRank={analysis.analysis?.ivData?.ivRank || 50} />
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-text-muted">IV Rank</span>
                <span className="font-mono">{analysis.analysis?.ivData?.ivRank}%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-text-muted">IV Percentile</span>
                <span className="font-mono">{analysis.analysis?.ivData?.ivPercentile}%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-text-muted">Current IV</span>
                <span className="font-mono">{(analysis.analysis?.ivData?.currentIV * 100)?.toFixed(1)}%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-text-muted">Historical Vol</span>
                <span className="font-mono">{(analysis.analysis?.ivData?.historicalVol * 100)?.toFixed(1)}%</span>
              </div>
            </div>
            <div className={`mt-4 p-3 rounded-lg ${
              analysis.analysis?.ivData?.ivRank >= 70 ? 'bg-accent-red/10 border border-accent-red/20' :
              analysis.analysis?.ivData?.ivRank <= 30 ? 'bg-accent-green/10 border border-accent-green/20' :
              'bg-accent-yellow/10 border border-accent-yellow/20'
            }`}>
              <p className={`text-xs ${
                analysis.analysis?.ivData?.ivRank >= 70 ? 'text-accent-red' :
                analysis.analysis?.ivData?.ivRank <= 30 ? 'text-accent-green' :
                'text-accent-yellow'
              }`}>
                {analysis.analysis?.ivData?.ivRank >= 70 
                  ? 'High IV - Favor selling premium'
                  : analysis.analysis?.ivData?.ivRank <= 30
                  ? 'Low IV - Favor buying premium'
                  : 'Normal IV - Either strategy viable'}
              </p>
            </div>
          </div>

          {/* Market Regime */}
          <div className="card p-6">
            <h3 className="font-display text-lg font-semibold mb-4 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-accent-blue" />
              Market Regime
            </h3>
            <div className="space-y-4">
              <div className="text-center p-4 bg-bg-tertiary rounded-xl">
                <div className={`text-2xl font-bold mb-1 ${
                  analysis.analysis?.regime?.trend === 'bullish' ? 'text-accent-green' :
                  analysis.analysis?.regime?.trend === 'bearish' ? 'text-accent-red' :
                  'text-accent-yellow'
                }`}>
                  {analysis.analysis?.regime?.trend?.toUpperCase()}
                </div>
                <div className="text-text-muted text-sm capitalize">
                  {analysis.analysis?.regime?.regime} Market
                </div>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-text-muted">Current Price</span>
                  <span className="font-mono">${analysis.analysis?.regime?.currentPrice?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-text-muted">Trend Strength</span>
                  <span className="font-mono">{analysis.analysis?.regime?.strength}%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-text-muted">EMA 10</span>
                  <span className="font-mono">${analysis.analysis?.regime?.ema10?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-text-muted">EMA 20</span>
                  <span className="font-mono">${analysis.analysis?.regime?.ema20?.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Trade Recommendation */}
          <div className="card p-6">
            <h3 className="font-display text-lg font-semibold mb-4 flex items-center gap-2">
              <Target className="w-5 h-5 text-accent-green" />
              Recommendation
            </h3>
            <div className={`text-center p-4 rounded-xl mb-4 ${
              analysis.recommendation === 'TRADE' ? 'bg-accent-green/10 border border-accent-green/20' :
              analysis.recommendation === 'CAUTION' ? 'bg-accent-yellow/10 border border-accent-yellow/20' :
              'bg-accent-red/10 border border-accent-red/20'
            }`}>
              <div className={`text-2xl font-bold ${
                analysis.recommendation === 'TRADE' ? 'text-accent-green' :
                analysis.recommendation === 'CAUTION' ? 'text-accent-yellow' :
                'text-accent-red'
              }`}>
                {analysis.recommendation}
              </div>
              {analysis.strategy && (
                <div className="text-text-muted text-sm mt-1">
                  {analysis.strategy.name}
                </div>
              )}
            </div>
            {analysis.tradePlan && (
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-text-muted">DTE Range</span>
                  <span className="font-mono">{analysis.tradePlan.entry?.dte?.join('-')} days</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-text-muted">Max Contracts</span>
                  <span className="font-mono">{analysis.tradePlan.entry?.maxContracts}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-text-muted">Profit Target</span>
                  <span className="font-mono text-accent-green">{analysis.tradePlan.exit?.profitTarget}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-text-muted">Stop Loss</span>
                  <span className="font-mono text-accent-red">{analysis.tradePlan.exit?.stopLoss}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-text-muted">Max Risk</span>
                  <span className="font-mono">{analysis.tradePlan.risk?.maxRisk}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Warnings */}
      {analysis?.warnings?.length > 0 && (
        <div className="card p-4 mb-6 bg-accent-yellow/5 border-accent-yellow/20">
          <h4 className="font-semibold text-accent-yellow mb-2 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Warnings
          </h4>
          <ul className="space-y-1">
            {analysis.warnings.map((warning, i) => (
              <li key={i} className="text-text-secondary text-sm flex items-start gap-2">
                <span className="text-accent-yellow">-</span>
                {warning}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Available Strategies */}
      {analysis?.alternatives?.length > 0 && (
        <div className="mb-6">
          <h3 className="font-display text-lg font-semibold mb-4">Alternative Strategies</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {analysis.alternatives.map((strat, i) => (
              <StrategyCard
                key={i}
                strategy={strat}
                isSelected={selectedStrategy?.name === strat.name}
                onClick={() => setSelectedStrategy(strat)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Scanner Results */}
      {showScanner && scanResults.length > 0 && (
        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-lg font-semibold flex items-center gap-2">
              <Search className="w-5 h-5 text-accent-blue" />
              Market Scanner Results
            </h3>
            <button
              onClick={() => setShowScanner(false)}
              className="text-text-muted hover:text-text-primary"
            >
              Hide
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left text-text-muted text-sm border-b border-border-color">
                  <th className="py-3 px-4">Symbol</th>
                  <th className="py-3 px-4">Price</th>
                  <th className="py-3 px-4">IV Rank</th>
                  <th className="py-3 px-4">Trend</th>
                  <th className="py-3 px-4">Regime</th>
                  <th className="py-3 px-4">Strategy</th>
                  <th className="py-3 px-4"></th>
                </tr>
              </thead>
              <tbody>
                {scanResults.map((result, i) => (
                  <ScannerRow
                    key={i}
                    result={result}
                    onAnalyze={(sym) => { setSymbol(sym); analyzeSymbol(sym); setShowScanner(false); }}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Strategy Reference Guide */}
      <div className="card p-6 mt-6">
        <h3 className="font-display text-lg font-semibold mb-4 flex items-center gap-2">
          <Info className="w-5 h-5 text-accent-blue" />
          Strategy Selection Guide
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-text-muted border-b border-border-color">
                <th className="py-3 px-4">Market View</th>
                <th className="py-3 px-4">Low IV (&lt;30)</th>
                <th className="py-3 px-4">Normal IV (30-70)</th>
                <th className="py-3 px-4">High IV (&gt;70)</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-border-color">
                <td className="py-3 px-4 font-semibold text-accent-green">Bullish</td>
                <td className="py-3 px-4">Long Calls, Call Debit Spreads</td>
                <td className="py-3 px-4">Call Debit Spreads</td>
                <td className="py-3 px-4">Put Credit Spreads</td>
              </tr>
              <tr className="border-b border-border-color">
                <td className="py-3 px-4 font-semibold text-accent-red">Bearish</td>
                <td className="py-3 px-4">Long Puts, Put Debit Spreads</td>
                <td className="py-3 px-4">Put Debit Spreads</td>
                <td className="py-3 px-4">Call Credit Spreads</td>
              </tr>
              <tr>
                <td className="py-3 px-4 font-semibold text-accent-yellow">Neutral</td>
                <td className="py-3 px-4 text-text-muted">Stay Out</td>
                <td className="py-3 px-4">Iron Condors (cautiously)</td>
                <td className="py-3 px-4">Iron Condors, Strangles</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
          <div className="p-4 bg-bg-tertiary rounded-xl">
            <h4 className="font-semibold mb-2 flex items-center gap-2">
              <Clock className="w-4 h-4 text-accent-blue" />
              DTE Guidelines
            </h4>
            <ul className="text-sm text-text-secondary space-y-1">
              <li>0-3 DTE: Scalps only</li>
              <li>5-14 DTE: Directional plays</li>
              <li>21-45 DTE: Premium selling</li>
              <li>&gt;60 DTE: Avoid for day trades</li>
            </ul>
          </div>
          <div className="p-4 bg-bg-tertiary rounded-xl">
            <h4 className="font-semibold mb-2 flex items-center gap-2">
              <Target className="w-4 h-4 text-accent-green" />
              Delta Targets
            </h4>
            <ul className="text-sm text-text-secondary space-y-1">
              <li>Long trades: 0.40+ delta</li>
              <li>Scalps: 0.60+ delta</li>
              <li>Credit spreads: 0.20-0.30 short</li>
              <li>ATM: 0.50 delta</li>
            </ul>
          </div>
          <div className="p-4 bg-bg-tertiary rounded-xl">
            <h4 className="font-semibold mb-2 flex items-center gap-2">
              <Shield className="w-4 h-4 text-accent-red" />
              Risk Rules
            </h4>
            <ul className="text-sm text-text-secondary space-y-1">
              <li>Max 2% per trade</li>
              <li>Max 5% per idea</li>
              <li>Long stop: 50% loss</li>
              <li>Credit stop: 2x premium</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Economic Calendar Warning */}
      {calendar?.events?.length > 0 && (
        <div className="card p-6 mt-6">
          <h3 className="font-display text-lg font-semibold mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-accent-yellow" />
            Upcoming Events
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {calendar.events.slice(0, 4).map((event, i) => (
              <div key={i} className={`p-3 rounded-lg border ${
                event.impact === 'high' ? 'border-accent-red/30 bg-accent-red/5' :
                'border-border-color bg-bg-tertiary'
              }`}>
                <div className="text-sm text-text-muted">{event.date}</div>
                <div className="font-semibold">
                  {event.type === 'earnings' ? event.symbol : event.event}
                </div>
                <div className={`text-xs ${event.impact === 'high' ? 'text-accent-red' : 'text-text-muted'}`}>
                  {event.impact} impact
                </div>
              </div>
            ))}
          </div>
          {calendar.warnings?.length > 0 && (
            <div className="mt-4 p-3 bg-accent-yellow/10 border border-accent-yellow/20 rounded-lg">
              <ul className="text-sm text-accent-yellow space-y-1">
                {calendar.warnings.map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Automated Options Trading Bot Configuration */}
      <div className="card p-6 mt-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Bot className="w-6 h-6 text-accent-orange" />
            <div>
              <h3 className="font-display text-lg font-semibold">Automated Options Trading</h3>
              <p className="text-text-muted text-sm">Configure the bot to trade options automatically</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowBotConfig(!showBotConfig)}
              className="btn-secondary flex items-center gap-2"
            >
              <Settings className="w-4 h-4" />
              {showBotConfig ? 'Hide Settings' : 'Show Settings'}
            </button>
            <button
              onClick={() => setBotConfig(prev => ({ ...prev, optionsEnabled: !prev.optionsEnabled }))}
              className={`relative w-14 h-7 rounded-full transition-all ${
                botConfig.optionsEnabled ? 'bg-accent-orange' : 'bg-bg-tertiary'
              }`}
            >
              <div className={`absolute top-1 w-5 h-5 rounded-full bg-white transition-all ${
                botConfig.optionsEnabled ? 'left-8' : 'left-1'
              }`} />
            </button>
          </div>
        </div>

        {/* Status indicator */}
        <div className={`p-3 rounded-lg mb-4 ${
          botConfig.optionsEnabled 
            ? 'bg-accent-orange/10 border border-accent-orange/20' 
            : 'bg-bg-tertiary'
        }`}>
          <div className="flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${botConfig.optionsEnabled ? 'bg-accent-orange animate-pulse' : 'bg-text-muted'}`} />
            <span className={botConfig.optionsEnabled ? 'text-accent-orange' : 'text-text-muted'}>
              {botConfig.optionsEnabled ? 'Options bot is ENABLED' : 'Options bot is disabled'}
            </span>
          </div>
        </div>

        {showBotConfig && (
          <div className="space-y-6">
            {/* Strategy Preferences */}
            <div>
              <h4 className="font-semibold mb-3">Strategy Preferences</h4>
              <label className="flex items-center justify-between p-3 bg-bg-tertiary rounded-lg">
                <div>
                  <span className="font-medium">Defined Risk Only</span>
                  <p className="text-xs text-text-muted">Only trade strategies with limited max loss</p>
                </div>
                <button
                  onClick={() => updateOptionsConfig('preferDefinedRisk', !botConfig.optionsConfig.preferDefinedRisk)}
                  className={`relative w-12 h-6 rounded-full transition-all ${
                    botConfig.optionsConfig.preferDefinedRisk ? 'bg-accent-green' : 'bg-bg-card'
                  }`}
                >
                  <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${
                    botConfig.optionsConfig.preferDefinedRisk ? 'left-7' : 'left-1'
                  }`} />
                </button>
              </label>
            </div>

            {/* IV Rules */}
            <div>
              <h4 className="font-semibold mb-3">Volatility Rules</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-text-muted mb-1">Max IV for Buying (0-100)</label>
                  <input 
                    type="number" 
                    value={botConfig.optionsConfig.maxIVRankForBuying}
                    onChange={(e) => updateOptionsConfig('maxIVRankForBuying', parseInt(e.target.value))}
                    min={40} max={90}
                    className="input text-sm py-1.5"
                  />
                  <p className="text-xs text-text-muted mt-1">Don't buy options above this IV rank</p>
                </div>
                <div>
                  <label className="block text-xs text-text-muted mb-1">Min IV for Selling (0-100)</label>
                  <input 
                    type="number" 
                    value={botConfig.optionsConfig.minIVRankForSelling}
                    onChange={(e) => updateOptionsConfig('minIVRankForSelling', parseInt(e.target.value))}
                    min={10} max={50}
                    className="input text-sm py-1.5"
                  />
                  <p className="text-xs text-text-muted mt-1">Don't sell premium below this IV rank</p>
                </div>
              </div>
            </div>

            {/* DTE Rules */}
            <div>
              <h4 className="font-semibold mb-3">Expiration Rules (DTE)</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-text-muted mb-1">Min DTE</label>
                  <input 
                    type="number" 
                    value={botConfig.optionsConfig.minDTE}
                    onChange={(e) => updateOptionsConfig('minDTE', parseInt(e.target.value))}
                    min={0} max={30}
                    className="input text-sm py-1.5"
                  />
                </div>
                <div>
                  <label className="block text-xs text-text-muted mb-1">Max DTE</label>
                  <input 
                    type="number" 
                    value={botConfig.optionsConfig.maxDTE}
                    onChange={(e) => updateOptionsConfig('maxDTE', parseInt(e.target.value))}
                    min={7} max={90}
                    className="input text-sm py-1.5"
                  />
                </div>
              </div>
            </div>

            {/* Risk Management */}
            <div>
              <h4 className="font-semibold mb-3">Risk Management</h4>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs text-text-muted mb-1">Max Risk Per Trade %</label>
                  <input 
                    type="number" 
                    value={botConfig.optionsConfig.maxRiskPerTrade * 100}
                    onChange={(e) => updateOptionsConfig('maxRiskPerTrade', parseFloat(e.target.value) / 100)}
                    min={0.5} max={5} step={0.5}
                    className="input text-sm py-1.5"
                  />
                </div>
                <div>
                  <label className="block text-xs text-text-muted mb-1">Max Options Allocation %</label>
                  <input 
                    type="number" 
                    value={botConfig.optionsConfig.maxOptionsAllocation * 100}
                    onChange={(e) => updateOptionsConfig('maxOptionsAllocation', parseFloat(e.target.value) / 100)}
                    min={5} max={50} step={5}
                    className="input text-sm py-1.5"
                  />
                </div>
                <div>
                  <label className="block text-xs text-text-muted mb-1">Max Contracts/Trade</label>
                  <input 
                    type="number" 
                    value={botConfig.optionsConfig.maxContractsPerTrade}
                    onChange={(e) => updateOptionsConfig('maxContractsPerTrade', parseInt(e.target.value))}
                    min={1} max={50}
                    className="input text-sm py-1.5"
                  />
                </div>
              </div>
            </div>

            {/* Exit Rules */}
            <div>
              <h4 className="font-semibold mb-3">Exit Rules</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-bg-tertiary rounded-lg">
                  <h5 className="text-sm font-medium text-accent-green mb-2">Long Options (Buying)</h5>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs text-text-muted mb-1">Profit Target %</label>
                      <input 
                        type="number" 
                        value={botConfig.optionsConfig.longProfitTarget * 100}
                        onChange={(e) => updateOptionsConfig('longProfitTarget', parseFloat(e.target.value) / 100)}
                        min={20} max={200} step={10}
                        className="input text-sm py-1"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-text-muted mb-1">Stop Loss %</label>
                      <input 
                        type="number" 
                        value={botConfig.optionsConfig.longStopLoss * 100}
                        onChange={(e) => updateOptionsConfig('longStopLoss', parseFloat(e.target.value) / 100)}
                        min={20} max={80} step={10}
                        className="input text-sm py-1"
                      />
                    </div>
                  </div>
                </div>
                <div className="p-3 bg-bg-tertiary rounded-lg">
                  <h5 className="text-sm font-medium text-accent-red mb-2">Credit Spreads (Selling)</h5>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs text-text-muted mb-1">Profit Target %</label>
                      <input 
                        type="number" 
                        value={botConfig.optionsConfig.creditProfitTarget * 100}
                        onChange={(e) => updateOptionsConfig('creditProfitTarget', parseFloat(e.target.value) / 100)}
                        min={30} max={90} step={10}
                        className="input text-sm py-1"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-text-muted mb-1">Stop Multiplier</label>
                      <input 
                        type="number" 
                        value={botConfig.optionsConfig.creditStopMultiplier}
                        onChange={(e) => updateOptionsConfig('creditStopMultiplier', parseFloat(e.target.value))}
                        min={1} max={3} step={0.5}
                        className="input text-sm py-1"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Liquidity Requirements */}
            <div>
              <h4 className="font-semibold mb-3">Liquidity Requirements</h4>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs text-text-muted mb-1">Min Open Interest</label>
                  <input 
                    type="number" 
                    value={botConfig.optionsConfig.minOpenInterest}
                    onChange={(e) => updateOptionsConfig('minOpenInterest', parseInt(e.target.value))}
                    min={100} max={2000} step={100}
                    className="input text-sm py-1.5"
                  />
                </div>
                <div>
                  <label className="block text-xs text-text-muted mb-1">Max Spread %</label>
                  <input 
                    type="number" 
                    value={botConfig.optionsConfig.maxSpreadPercent * 100}
                    onChange={(e) => updateOptionsConfig('maxSpreadPercent', parseFloat(e.target.value) / 100)}
                    min={5} max={20}
                    className="input text-sm py-1.5"
                  />
                </div>
                <div>
                  <label className="block text-xs text-text-muted mb-1">Min Volume</label>
                  <input 
                    type="number" 
                    value={botConfig.optionsConfig.minVolume}
                    onChange={(e) => updateOptionsConfig('minVolume', parseInt(e.target.value))}
                    min={50} max={500}
                    className="input text-sm py-1.5"
                  />
                </div>
              </div>
            </div>

            {/* Timing */}
            <div>
              <h4 className="font-semibold mb-3">Timing Rules</h4>
              <div className="space-y-3">
                <label className="flex items-center justify-between p-3 bg-bg-tertiary rounded-lg">
                  <div>
                    <span className="font-medium">Avoid First 15 Minutes</span>
                    <p className="text-xs text-text-muted">Skip trading during market open volatility</p>
                  </div>
                  <button
                    onClick={() => updateOptionsConfig('avoidFirst15Minutes', !botConfig.optionsConfig.avoidFirst15Minutes)}
                    className={`relative w-12 h-6 rounded-full transition-all ${
                      botConfig.optionsConfig.avoidFirst15Minutes ? 'bg-accent-blue' : 'bg-bg-card'
                    }`}
                  >
                    <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${
                      botConfig.optionsConfig.avoidFirst15Minutes ? 'left-7' : 'left-1'
                    }`} />
                  </button>
                </label>
                <label className="flex items-center justify-between p-3 bg-bg-tertiary rounded-lg">
                  <div>
                    <span className="font-medium">Close Before Expiry</span>
                    <p className="text-xs text-text-muted">Auto-close positions before expiration</p>
                  </div>
                  <button
                    onClick={() => updateOptionsConfig('closeBeforeExpiry', !botConfig.optionsConfig.closeBeforeExpiry)}
                    className={`relative w-12 h-6 rounded-full transition-all ${
                      botConfig.optionsConfig.closeBeforeExpiry ? 'bg-accent-yellow' : 'bg-bg-card'
                    }`}
                  >
                    <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${
                      botConfig.optionsConfig.closeBeforeExpiry ? 'left-7' : 'left-1'
                    }`} />
                  </button>
                </label>
              </div>
            </div>

            {/* Info Box */}
            <div className="p-4 bg-accent-orange/10 border border-accent-orange/20 rounded-lg">
              <h4 className="font-semibold text-accent-orange mb-2">Options Trading Rules</h4>
              <ul className="text-xs text-text-secondary space-y-1">
                <li>IV Rank &gt; 70: Favor selling premium (credit spreads)</li>
                <li>IV Rank &lt; 30: Favor buying premium (long calls/puts)</li>
                <li>21-45 DTE: Optimal for premium selling strategies</li>
                <li>5-14 DTE: Optimal for directional plays</li>
                <li>Delta 0.40+: Required for long options</li>
                <li>Delta 0.20-0.30: Target for credit spread short legs</li>
              </ul>
            </div>

            {/* Save Button */}
            <button
              onClick={saveBotConfig}
              disabled={savingBotConfig}
              className="btn-primary w-full flex items-center justify-center gap-2"
            >
              {savingBotConfig ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              {savingBotConfig ? 'Saving...' : 'Save Options Bot Settings'}
            </button>
          </div>
        )}
      </div>
      </div>
    </div>
  );
};

export default Options;
